import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';

const SocialMetricsSection = ({ onUpdate, boostInput = false }) => {
  const [currentSocialMetric, setCurrentSocialMetric] = useState('');
  const [currentSocialOperator, setCurrentSocialOperator] = useState('');
  const [currentSocialThreshold, setCurrentSocialThreshold] = useState('');
  const [boost, setBoost] = useState(1);

  const operatorOptions = [
    { id: '<=', label: '<=' },
    { id: '>=', label: '>=' }
  ];

  const socialMetricOptions = [
    { id: 'score_popular', label: 'Score Popular', type: 'score' },
    { id: 'score_trending', label: 'Score Trending', type: 'score' },
    { id: 'num_like', label: 'Num Like', type: 'num' },
    { id: 'num_share', label: 'Num Share', type: 'num' },
    { id: 'num_comment', label: 'Num Comment', type: 'num' },
    { id: 'num_follower', label: 'Num Follower', type: 'num' }
  ];

  const handleSocialMetricChange = (event) => {
    setCurrentSocialMetric(event.target.value);
    
    // Set default operator and threshold based on metric type
    setCurrentSocialOperator('>=');
    if (event.target.value === 'num_follower') {
      setCurrentSocialThreshold('100');
    } else {
      setCurrentSocialThreshold('1');
    }
  };

  const handleSocialMetricsAction = (actionType) => {
    if (!currentSocialMetric || !currentSocialOperator || !currentSocialThreshold) return;
    
    const selectedMetric = socialMetricOptions.find(m => m.id === currentSocialMetric);
    let thresholdValue;
    
    if (selectedMetric.type === 'score') {
      thresholdValue = parseFloat(currentSocialThreshold);
      if (isNaN(thresholdValue)) return;
    } else if (selectedMetric.type === 'num') {
      thresholdValue = parseInt(currentSocialThreshold);
      if (isNaN(thresholdValue)) return;
    }
    
    const updateData = {
      action: actionType,
      filter: 'numeric',
      field: currentSocialMetric,
      value: thresholdValue,
      operator: currentSocialOperator,
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    
    // Reset after adding
    setCurrentSocialMetric('');
    setCurrentSocialOperator('');
    setCurrentSocialThreshold('');
  };

  const getThresholdLabel = () => {
    if (!currentSocialMetric) return 'number';
    const selectedMetric = socialMetricOptions.find(m => m.id === currentSocialMetric);
    return selectedMetric?.type === 'score' ? 'float' : 'integer';
  };

  const getThresholdStep = () => {
    if (!currentSocialMetric) return '0.01';
    const selectedMetric = socialMetricOptions.find(m => m.id === currentSocialMetric);
    return selectedMetric?.type === 'score' ? '0.01' : '1';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Metric</InputLabel>
          <Select
            value={currentSocialMetric}
            label="Metric"
            onChange={handleSocialMetricChange}
          >
            {socialMetricOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={currentSocialOperator}
            label="Operator"
            onChange={(e) => setCurrentSocialOperator(e.target.value)}
          >
            {operatorOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={`Threshold (${getThresholdLabel()})`}
          type="number"
          value={currentSocialThreshold}
          onChange={(e) => setCurrentSocialThreshold(e.target.value)}
          inputProps={{ 
            step: getThresholdStep()
          }}
          sx={{ flex: 1 }}
        />
      </Box>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSocialMetricsAction('include')}
          disabled={!currentSocialMetric || !currentSocialOperator || !currentSocialThreshold}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleSocialMetricsAction('boost')}
            disabled={!currentSocialMetric || !currentSocialOperator || !currentSocialThreshold}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleSocialMetricsAction('exclude')}
          disabled={!currentSocialMetric || !currentSocialOperator || !currentSocialThreshold}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default SocialMetricsSection;

